import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, styled, Tooltip, Typography } from '@mui/material';
import { theme } from 'app/Theme';
import { SET_FILTERS_OVERVIEW_ATTENDANCE, SET_FILTERS_OVERVIEW_LEAVE } from 'dashboard/context.actionTypes';
import { useCallback } from 'react';
import useOverview from '../useOverviewProvider';

const SummaryBoxContainer = styled('div')(
    ({ theme: { spacing, palette, typography, shape } }) => ({
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        justifyContent: 'center',
        flex: 1,
        paddingBottom: spacing(2),
        cursor: "pointer",
        position: 'relative',

        '@media screen and (max-width: 1024px)': {
            flex: '1 1 220px',
            minWidth: 180
        }
    })
);

const StyledProgressWrapper = styled('div')(
    ({ theme: { spacing, palette, typography, shape } }) => ({
        height: spacing(.5),
        width: '100%',
        marginBottom: spacing(2)
    })
);

const StyledNumber = styled(Typography)(
    ({ theme: { spacing, palette, typography, shape } }) => ({
        paddingLeft: spacing(2),
        fontWeight: typography.fontWeightBold,
        marginBottom: spacing(0.5)
    })
);

const StyledDescription = styled(Typography)(
    ({ theme: { spacing, palette, typography, shape } }) => ({
        color: palette.quaternary.main,
        paddingLeft: spacing(2),

        '@media screen and (max-width: 768px)': {
            minWidth: '150px'
        }
    })
);

function SummaryBox({ data: { present, total, color, description, status, developerCount, qaCount, managerCount, projectManagerCount, designerCount, hrCount, adminCount, otherCount } }) {
    const progress = (present / total) * 100;
    const [{ }, dispatch] = useOverview();

    const handleClick = useCallback(() => {
        dispatch({
            type: status === 'leave'
                ? SET_FILTERS_OVERVIEW_LEAVE
                : SET_FILTERS_OVERVIEW_ATTENDANCE,
            payload: status
        });
    }, [status]);

    const positionTooltip = (
        <Box sx={{ py: 0.5 }}>
            <Typography variant='body2'>Developers: {developerCount}</Typography>
            <Typography variant='body2'>QAs: {qaCount}</Typography>
            <Typography variant='body2'>Managers: {managerCount}</Typography>
            <Typography variant='body2'>Project Managers: {projectManagerCount}</Typography>
            <Typography variant='body2'>Designers: {designerCount}</Typography>
            <Typography variant='body2'>HR: {hrCount}</Typography>
            <Typography variant='body2'>Admin & Support: {adminCount}</Typography>
            <Typography variant='body2'>Others: {otherCount}</Typography>
        </Box>
    );

    const content = (
        <SummaryBoxContainer sx={{ background: theme.palette[color].light }} onClick={handleClick}>
            {status === 'all' && developerCount !== undefined && (
                <Tooltip
                    arrow
                    placement='bottom-start'
                    componentsProps={{
                        tooltip: {
                            sx: {
                                backgroundColor: theme.palette.background.paper,
                                color: theme.palette.text.primary,
                                border: `1px solid ${theme.palette.divider}`,
                                boxShadow: theme.shadows[3]
                            }
                        },
                        arrow: {
                            sx: {
                                color: theme.palette.background.paper,
                                '&:before': {
                                    border: `1px solid ${theme.palette.divider}`
                                }
                            }
                        }
                    }}
                    title={positionTooltip}
                >
                    <Box sx={{ position: 'absolute', top: 12, right: 8, zIndex: 1 }}>
                        <InfoOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary', cursor: 'help' }} />
                    </Box>
                </Tooltip>
            )}
            <StyledProgressWrapper sx={{ background: theme.palette[color].main }}>
                <Box sx={{ height: "100%", background: theme.palette[color].dark, width: `${progress}%` }}></Box>
            </StyledProgressWrapper>
            <StyledNumber variant='h4' sx={{ color: theme.palette[color].dark }}>{present}</StyledNumber>
            <StyledDescription variant="subtitle2">{description}</StyledDescription>
        </SummaryBoxContainer>
    );

    return content;
}

export default SummaryBox;
